import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Loader2, RotateCcw, Save, ShieldCheck } from "lucide-react";

const LISTS = [
  ["translations", "Trusted Bible translations", "One per line. Only these are fetched and quoted."],
  ["commentaries", "Trusted commentaries", "One per line. Copyrighted works (Wiersbe, NAC) cannot be fetched online — they are listed for the admin's own cross-checking."],
  ["sites", "Trusted websites", "One per line. Evidence is fetched from these (BibleHub parallel/commentary/lexicon pages; truthaccordingtoscripture for Spurgeon)."],
];
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

/* Admin-editable trusted sources + rules that govern every Claude accuracy check. Collapsed by default. */
export default function AccuracyRules() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["accuracy-rules"], queryFn: async () => (await api.get("/admin/accuracy/rules")).data });
  useEffect(() => { if (data && !form) setForm({ translations: toLines(data.translations), commentaries: toLines(data.commentaries), sites: toLines(data.sites), rules: data.rules }); }, [data, form]);
  if (!data) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/accuracy/rules", { translations: fromLines(form.translations), commentaries: fromLines(form.commentaries), sites: fromLines(form.sites), rules: form.rules });
      qc.setQueryData(["accuracy-rules"], (d) => ({ ...d, ...saved }));
      toast.success("Accuracy rules saved — they apply to the next check.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = () => setForm({ translations: toLines(data.defaults.translations), commentaries: toLines(data.defaults.commentaries), sites: toLines(data.defaults.sites), rules: data.defaults.rules });

  return (
    <section className="rounded-2xl border border-border bg-card" data-testid="accuracy-rules">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-4 text-left" data-testid="accuracy-rules-toggle" aria-expanded={open}>
        <span className="inline-flex items-center gap-2 font-serif text-lg font-semibold text-foreground"><ShieldCheck className="h-4 w-4 text-gold" /> Trusted sources &amp; rules for accuracy checks</span>
        <span className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{data.translations.length} translations · {data.commentaries.length} commentaries · {data.sites.length} sites {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>
      {open && form && (
        <div className="space-y-5 border-t border-border p-5">
          <p className="text-sm text-muted-foreground">Every check fetches evidence live from these sources and instructs Claude to rely on <em>nothing else</em> — not its own memory, not the site's imported texts. Edit freely; changes apply to the next check.</p>
          <div className="grid gap-4 lg:grid-cols-3">
            {LISTS.map(([k, label, hint]) => (
              <label key={k} className="block">
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
                <Textarea rows={8} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 font-mono text-xs" data-testid={`accuracy-rules-${k}`} />
                <span className="mt-1 block text-[11px] leading-snug text-muted-foreground">{hint}</span>
              </label>
            ))}
          </div>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Rules Claude must follow</span>
            <Textarea rows={7} value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} className="mt-1 text-sm" data-testid="accuracy-rules-text" />
          </label>
          <p className="text-[11px] text-muted-foreground">Fetchable online right now — translations: {data.fetchable_translations.join(", ")} · commentaries: {data.fetchable_commentaries.join(", ")}. Anything else in your lists is kept for reference but cannot be quoted.</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="accuracy-rules-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save rules</Button>
            <Button variant="ghost" onClick={reset} className="rounded-full" data-testid="accuracy-rules-reset"><RotateCcw className="mr-2 h-4 w-4" /> Restore defaults</Button>
          </div>
        </div>
      )}
    </section>
  );
}
